import { ThunkMiddleware } from '@reduxjs/toolkit';
import { JSONObject, bind } from 'immer-yjs';
import type { DebouncedFunc } from 'lodash';
import throttle from 'lodash/throttle';
import { getPortalSdk } from 'portal-sdk';
import { isActionSynced } from 'redux-state-sync';
import { v4 as uuid } from 'uuid';
import { emptyObject } from 'vet-bones/constants';
import { isTesting } from 'vet-bones/utils';
import { PortalUser } from 'vet-bones/utils/portal';

import { notificationsActionCreators } from 'src/actions/notifications/actionCreators';
import {
  ProjectsAction,
  projectsActionCreators,
} from 'src/actions/projects/actionCreators';
import { ProjectsActionType } from 'src/actions/projects/actionTypes';
import {
  isDeletedLinkData,
  isDeletedNodeData,
  isResource,
} from 'src/containers/ProjectDiagram/utils/data';
import { CURRENT_MIGRATION_VERSION } from 'src/reducers/migrations/constants';
import { collaborationSlice } from 'src/store/collaboration/collaboration.slice';
import { Project } from 'src/store/state/projects';
import { getStore } from 'src/store/store';
import { ReduxState } from 'src/store/types';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as collaborationProvider from 'src/utils/collaboration/collaborationProvider';
import { isCollaborationConfigured } from 'src/utils/collaboration/config';
import { yDocToProject } from 'src/utils/collaboration/scbf/yDocToProject';
import { duplicateProject } from 'src/utils/duplicate/duplicateProject';
import {
  decodeCloudContent,
  encodeCloudContent,
} from 'src/utils/exportProject/encodeProject';
import { generateDateTimeId } from 'src/utils/generateId';
import { getCloudProjectQueryKey } from 'src/utils/hooks/useCloudStorageProject';
import { getCloudProjectListQueryKey } from 'src/utils/hooks/useCloudStorageProjects';
import { queryClient } from 'src/utils/queryClient';
import { getProjectRoute } from 'src/utils/routes/getProjectRoute';

const projectSaveFunctions = new Map<
  string,
  DebouncedFunc<
    (projectContents: string, connectionId: string | undefined) => Promise<void>
  >
>();

const getProjectSaveFunction = (projectId: string) => {
  if (!projectSaveFunctions.has(projectId)) {
    const saveFunction = throttle(
      async (projectContents: string, connectionId: string | undefined) => {
        try {
          const portalSdk = getPortalSdk();
          if (portalSdk) {
            await portalSdk.updateDesignerProject(
              projectId,
              projectContents,
              undefined,
              connectionId
            );
          }

          const store = getStore();
          store.dispatch(uiSlice.actions.updatedSaveState(false));
        } catch (err: any) {
          if (!err?.message?.includes('Project not found')) {
            console.error(err);
          }
          // check if more pending saves are in the queue
          // if so then just let them try again and if not then show the message
          const saveFunction = projectSaveFunctions.get(projectId);
          if (saveFunction?.flush) {
            saveFunction.flush();
          }

          // Block any pending throttled saves while in error state
          if (saveFunction?.cancel) {
            saveFunction.cancel();
          }
          projectSaveFunctions.delete(projectId);

          // copy the project to local project and then redirect to the newly created project
          const store = getStore();
          const state = store.getState();
          const cloudProject = state.projects.projectsById[projectId];
          const newLocalProjectId = generateDateTimeId();
          if (cloudProject) {
            const localProject = duplicateProject(cloudProject, {
              isCloudProject: false,
              id: newLocalProjectId,
              connectionId,
              dateCreated: Date.now(),
              cloudProjectId: projectId,
              cloudProjectLastUpdated: cloudProject.dateLastUpdated,
              diagram: {
                ...cloudProject.diagram,
                // new diagram id to force goJs refresh
                id: uuid(),
              },
            });

            store.dispatch(
              projectsActionCreators.backedUpCloudProjectToLocalProject(
                localProject
              )
            );

            // Navigate to the new local project and wait for navigation to complete
            await navigateToProject(newLocalProjectId);
          }
        }
      },
      isTesting() ? 500 : 5000, // shorten time in testing
      { trailing: true }
    );

    projectSaveFunctions.set(projectId, saveFunction);
  }
  const saveFunction = projectSaveFunctions.get(projectId);

  if (!saveFunction) {
    throw new Error('Error updating cloud project');
  }

  return saveFunction;
};

// beforeunload handler to save any pending changes
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    // Disconnect all collaboration WebSocket connections
    collaborationProvider.disconnectAll();

    projectSaveFunctions.forEach((saveFunction) => {
      if (saveFunction.flush) {
        saveFunction.flush();
      }
    });
  });
}

// Propagate collaboration auth state from the provider into Redux. These
// listeners are registered once at module init so they survive reconnects.
let collaborationAuthListenersRegistered = false;
const registerCollaborationAuthListeners = () => {
  if (collaborationAuthListenersRegistered) {
    return;
  }
  collaborationAuthListenersRegistered = true;

  collaborationProvider.addReadOnlyChangeEventListener(
    (projectId, readOnly) => {
      getStore().dispatch(
        collaborationSlice.actions.setCollaborationReadOnly({
          projectId,
          readOnly,
        })
      );
    }
  );

  collaborationProvider.addAuthFailureEventListener((projectId) => {
    const store = getStore();
    store.dispatch(
      collaborationSlice.actions.clearedCollaborationProject({ projectId })
    );
    store.dispatch(notificationsActionCreators.collaborationAuthFailed());
  });
};

/**
 * Flushes any pending throttled save for a cloud project.
 * This should be called before operations that need the latest cloud state,
 * such as duplicating a cloud project.
 */
export const flushPendingProjectSave = async (
  projectId: string
): Promise<void> => {
  const saveFunction = projectSaveFunctions.get(projectId);
  if (saveFunction?.flush) {
    await saveFunction.flush();
  }
};

// handle navigation in middleware
const navigateToProject = (projectId: string) => {
  if (typeof window !== 'undefined' && window.location) {
    const newPath = getProjectRoute(projectId);
    const basename = window.location.pathname;
    window.history.pushState(null, '', `${basename}#${newPath}`);
    // Instead of just dispatching popstate, we'll return a promise that resolves when navigation is complete
    return new Promise<void>((resolve) => {
      const handlePopState = () => {
        window.removeEventListener('popstate', handlePopState);
        // Give time for the state to update
        setTimeout(resolve, 0);
      };
      window.addEventListener('popstate', handlePopState);
      window.dispatchEvent(new PopStateEvent('popstate'));
    });
  }
  // Return a resolved promise if we're not in a browser environment
  return Promise.resolve();
};

const stripVolatile = (p: Project) => {
  const { dateLastUpdated: _dateLastUpdated, diagram, ...rest } = p;
  const { id: _diagramId, ...diagramRest } = diagram || {};
  return { ...rest, diagram: diagramRest };
};

const pruneDeletedProjectResources = (project: Project): Project => {
  if (!project?.diagram?.model) {
    return project;
  }

  const resourceIds = new Set(
    project.diagram.model.nodeDataArray
      .filter((nodeData) => isResource(nodeData))
      .map((nodeData) => nodeData.value)
  );

  const fileUploadsById = { ...project.fileUploadsById };
  const virtualGraphsById = { ...project.virtualGraphsById };

  Object.keys(fileUploadsById).forEach((fileUploadId) => {
    if (!resourceIds.has(fileUploadId)) {
      delete fileUploadsById[fileUploadId];
    }
  });

  Object.keys(virtualGraphsById).forEach((virtualGraphId) => {
    if (!resourceIds.has(virtualGraphId)) {
      delete virtualGraphsById[virtualGraphId];
    }
  });

  return {
    ...project,
    fileUploadsById,
    virtualGraphsById,
  };
};

const pruneDeletedProjectModels = (project: Project): Project => {
  if (!project?.diagram?.model?.modelData) {
    return project;
  }

  const { deletedModelIds } = project.diagram.model.modelData;

  const modelsById = { ...project.modelsById };
  Object.keys(modelsById).forEach((modelId) => {
    if (deletedModelIds?.includes(modelId)) {
      delete modelsById[modelId];
    }
  });

  const isDraft = Boolean(project.diagram.model.modelData.draftData);
  const nodeDataArray = isDraft
    ? project.diagram.model.nodeDataArray
    : project.diagram.model.nodeDataArray.filter(
        (data) => !isDeletedNodeData(data)
      );
  const linkDataArray = isDraft
    ? project.diagram.model.linkDataArray
    : project.diagram.model.linkDataArray.filter(
        (data) => !isDeletedLinkData(data)
      );

  return {
    ...project,
    diagram: {
      ...project.diagram,
      model: {
        ...project.diagram.model,
        linkDataArray,
        nodeDataArray,
        modelData: {
          ...project.diagram.model.modelData,
          deletedModelIds: [],
        },
      },
    },
    modelsById,
  };
};

// Tracks the last time a RECEIVED_COLLABORATION_UPDATE was processed per project.
// Used to suppress the GoJS echo: when collaboration merges new data into GoJS,
// GoJS fires UPDATED_PROJECT_DIAGRAM back, which would write to Y.Doc and
// bounce between windows. We skip Y.Doc writes for a short window after a
// collaboration update to break this cycle.
const lastCollaborationUpdateByProject = new Map<string, number>();
const COLLABORATION_ECHO_WINDOW_MS = 500;

export const cloudProjectMiddleware: ThunkMiddleware<ReduxState> =
  (store) => (next) => async (action: ProjectsAction) => {
    // Drop cross-tab sync of diagram updates when collaboration is active
    if (
      isActionSynced(action) &&
      action.type === ProjectsActionType.UPDATED_PROJECT_DIAGRAM &&
      action.payload?.id &&
      collaborationProvider.isConnected(action.payload.id)
    ) {
      return undefined;
    }

    const isInitialLoadOfCloudProject =
      action.type === ProjectsActionType.LOADED_PROJECT_FROM_CLOUD &&
      !store.getState().projects.projectsById[action.payload.project.id];

    const portalUser = queryClient.getQueriesData({
      queryKey: 'usePortalUser',
    })?.[0]?.[1] as PortalUser;

    const isDesignerStorageEnabled = portalUser?.is_designer_storage_enabled;

    // Prune models if initial load of cloud project
    if (
      isInitialLoadOfCloudProject &&
      action.payload &&
      isDesignerStorageEnabled
    ) {
      const prunedProject = pruneDeletedProjectModels(action.payload.project);
      const modifiedAction = {
        ...action,
        payload: {
          ...action.payload,
          project: prunedProject,
        },
      };
      const result = await next(modifiedAction);

      // Connect to collaboration service if enabled
      const projectId = action.payload.project.id;
      if (
        Boolean(portalUser?.is_collaboration_enabled) &&
        isCollaborationConfigured()
      ) {
        registerCollaborationAuthListeners();
        try {
          collaborationProvider.connect({
            projectId,
            onRemoteUpdate: (doc) => {
              const project = yDocToProject(doc);
              store.dispatch(
                projectsActionCreators.receivedCollaborationUpdate(
                  projectId,
                  project
                )
              );
            },
          });
        } catch (err) {
          // Connection failure should not block project loading —
          // the legacy save path will be used as fallback.
          console.warn(
            'Collaboration connection failed, using legacy save',
            err
          );
        }
      }

      return result;
    }
    // if there is no id, action is not cloud project related
    const { payload } = action;
    if (
      !payload ||
      typeof payload !== 'object' ||
      !('id' in payload) ||
      !payload.id ||
      !isDesignerStorageEnabled
    ) {
      return next(action);
    }
    const payloadId = payload.id;

    const portalSdk = getPortalSdk();

    // duplicate & rename project need the cloud project in the store before it can be duplicated
    // these situations are to handle the home page when cloud projects are not in state
    if (
      action.type === ProjectsActionType.DUPLICATED_LOCAL_PROJECT ||
      action.type === ProjectsActionType.RENAMED_PROJECT ||
      action.type === ProjectsActionType.MIGRATED_CLOUD_PROJECT_TO_LOCAL ||
      action.type === ProjectsActionType.MIGRATED_LOCAL_PROJECT_BACKUP_TO_CLOUD
    ) {
      let cloudProject;
      // Ensure any pending throttled updates are sent before getting project
      const saveFunction = projectSaveFunctions.get(payloadId);
      if (saveFunction?.flush) {
        await saveFunction.flush();
      }

      try {
        cloudProject = await portalSdk?.getDesignerProject(payloadId);
      } catch (err: any) {
        // This is the error we will get if the project is a local project
        if (!err?.message?.includes('is not a valid UUID.')) {
          console.error('Error fetching cloud project', err);
        }
      }

      if (cloudProject?.content) {
        const data = decodeCloudContent(cloudProject.content);
        const newCloudProject = Object.values(
          data?.projects?.projectsById || emptyObject
        )[0] as Project | undefined;

        if (newCloudProject) {
          const newProject = duplicateProject(newCloudProject, {
            id: cloudProject.id,
            connectionId: cloudProject.connection_id || undefined,
            name: cloudProject.name,
            isCloudProject: true,
            dateLastUpdated: new Date(cloudProject.updated_at).getTime(),
            dateCreated: new Date(cloudProject.created_at).getTime(),
            voiceboxEvaluatedCompetencyQuestions: undefined,
            voiceboxEvaluatedSummary: undefined,
          });
          store.dispatch(
            projectsActionCreators.loadedProjectFromCloud(newProject)
          );

          if (action.type === ProjectsActionType.RENAMED_PROJECT) {
            try {
              await portalSdk?.renameDesignerProject(
                payloadId,
                action.payload.name
              );
              queryClient.refetchQueries(getCloudProjectListQueryKey());
              queryClient.refetchQueries(
                getCloudProjectQueryKey(cloudProject.id)
              );
            } catch (err) {
              console.error('Error renaming cloud project', err);
              // must wait for previous action middleware to finish completely
              // before renaming project to avoid cloud sync issues
              await store.dispatch(async (wrappedDispatch) => {
                wrappedDispatch(
                  projectsActionCreators.clearedProjectVoiceboxEvaluation({
                    id: payloadId,
                  })
                );
              });
              // Revert the name change in local state
              store.dispatch(
                projectsActionCreators.renamedProject({
                  id: payloadId,
                  name: cloudProject.name,
                })
              );
            }
          }
        }
      }
    }

    const oldProject = store.getState().projects.projectsById[payloadId];
    const actionResult = await next(action);

    // isActionSynced(action) indicates that the action is triggered in a different tab so it will be sent there
    // RECEIVED_COLLABORATION_UPDATE is already from a remote Y.Doc update; do not re-write to Y.Doc.
    if (
      (oldProject && !oldProject.isCloudProject) ||
      (oldProject && oldProject.isRemoved) ||
      !portalSdk ||
      isActionSynced(action) ||
      action.type === ProjectsActionType.RECEIVED_COLLABORATION_UPDATE
    ) {
      if (action.type === ProjectsActionType.RECEIVED_COLLABORATION_UPDATE) {
        lastCollaborationUpdateByProject.set(payloadId, Date.now());
      }
      return actionResult;
    }
    if (action.type === ProjectsActionType.ARCHIVED_PROJECT) {
      // Disconnect from collaboration if active
      collaborationProvider.disconnect(payloadId);
      store.dispatch(
        collaborationSlice.actions.clearedCollaborationProject({
          projectId: payloadId,
        })
      );

      // Flush any pending throttled save so changes are persisted before
      // archiving, then return early to avoid the change-detection path
      // scheduling a new save against the now-archived project.
      const pendingSave = projectSaveFunctions.get(payloadId);
      if (pendingSave?.flush) {
        await pendingSave.flush();
      }
      projectSaveFunctions.delete(payloadId);

      try {
        await portalSdk.archiveDesignerProject(payloadId);
        queryClient.refetchQueries(getCloudProjectListQueryKey());
      } catch (err) {
        console.error('Error deleting cloud project', err);
      }
      return actionResult;
    }

    if (action.type === ProjectsActionType.RESTORED_CLOUD_PROJECT) {
      try {
        await portalSdk.restoreDesignerProject(payloadId);
        queryClient.refetchQueries(getCloudProjectListQueryKey());
      } catch (err) {
        console.error('Error restoring cloud project', err);
      }
    }

    const updatedProject: Project | undefined =
      store.getState().projects.projectsById[payloadId];

    if (updatedProject?.isCloudProject) {
      const prunedProject = pruneDeletedProjectResources(updatedProject);

      const hasChanged =
        oldProject &&
        oldProject?.isCloudProject &&
        JSON.stringify(stripVolatile(prunedProject)) !==
          JSON.stringify(
            stripVolatile(pruneDeletedProjectResources(oldProject))
          );

      if (hasChanged) {
        const projectId = payloadId;

        // If collaboration is active, write to Y.Doc instead of saving directly
        if (collaborationProvider.isConnected(projectId)) {
          // Suppress GoJS echo: when a RECEIVED_COLLABORATION_UPDATE merges
          // remote data into Redux, GoJS reacts and fires UPDATED_PROJECT_DIAGRAM
          // back. Writing that echo to Y.Doc would bounce between windows.
          const lastCollabUpdate =
            lastCollaborationUpdateByProject.get(projectId) ?? 0;
          if (Date.now() - lastCollabUpdate < COLLABORATION_ECHO_WINDOW_MS) {
            return actionResult;
          }
          const doc = collaborationProvider.getDoc(projectId);
          if (doc) {
            const rootMap = doc.getMap('project');
            const binder = bind<JSONObject>(rootMap);
            doc.transact(() => {
              binder.update(() => {
                return { ...prunedProject } as unknown as JSONObject;
              });
            }, 'local');
            binder.unbind();
          }
          // SCS handles persistence — no local save indicator needed
        } else {
          // Legacy save path — encode and save via portal SDK
          const updatedProjectEncoded =
            encodeCloudContent(prunedProject, CURRENT_MIGRATION_VERSION) || '';

          // Set saving state immediately when changes are detected
          store.dispatch(uiSlice.actions.updatedSaveState(true));

          // The function will execute at most once every 5 seconds
          const saveFunction = getProjectSaveFunction(projectId);
          saveFunction(updatedProjectEncoded, updatedProject?.connectionId);
        }
      }
    }

    return actionResult;
  };
